/* eslint-disable @typescript-eslint/no-explicit-any */
import { types, flow, Instance } from 'mobx-state-tree';
import {
  SubscriptionPlanModel,
  ISubscriptionPlanModel,
  LicenseInfoModel,
  ILicenseInfoModel,
  InvoiceModel,
  IInvoiceModel,
  CurrentPlanModel,
  UpgradeLicensePlan,
  ICurrentPlanModel,
} from './models';
import { SubscriptionPlanResponse, UpgradeLicenseRespData, StripeACHResponse } from './types';
import { BillingInput, UpgradeLicenseFormInput } from 'components/Forms';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { IPreviousLicenseInfo, PreviousLicenseInfo } from './models/UpgradeLicense.model';
import { FeatureInfoModel, IFeatureInfoModel } from './models/featureInfo.model';
import { AddonLicenseInfoModel, IAddonLicenseInfoModel } from './models/licenseInfo.model';

const { model, array, maybeNull, enumeration, maybe, reference } = types;

export const UpgradeLicenseStore = model('UpgradeLicenseStore', {
  lisenceInfo: maybeNull(LicenseInfoModel),
  addonInfo: maybeNull(AddonLicenseInfoModel),
  featureData: maybe(FeatureInfoModel),
  subscriptionPlans: array(SubscriptionPlanModel),
  selectedPlan: maybe(reference(SubscriptionPlanModel)),
  currentPlan: maybe(CurrentPlanModel),
  upgradelicenseplan: array(UpgradeLicensePlan),
  invoice: maybe(InvoiceModel),
  previousLicense: maybeNull(PreviousLicenseInfo),
  step: enumeration('Steps', ['upgrade-form', 'payment-dialog', 'payment-form']),
  paymentMethod: enumeration('PaymentMethods', ['card', 'ach', 'invoice']),
})
  .views((self) => ({
    get defaultValues(): { planId: number; periodId: number } {
      let planId = 0;
      let periodId = 0;
      const currentExistInAvailable = self.subscriptionPlans.find(({ id }) => id === self.selectedPlan?.id);
      if (currentExistInAvailable) {
        planId = self.currentPlan?.id || 0;
        periodId = self.currentPlan?.period.id || 0;
      }
      return { planId, periodId };
    },
  }))
  .actions((self) => ({
    normalizeSubscriptionPlans: ({
      data: { data },
    }: AxiosResponse<{ data: SubscriptionPlanResponse }>): ISubscriptionPlanModel[] => {
      self.subscriptionPlans.replace(data.available as ISubscriptionPlanModel[]);
      self.currentPlan = data.current as ICurrentPlanModel;
      self.selectedPlan = self.subscriptionPlans.find(({ id }) => id === self.currentPlan?.id);
      return self.subscriptionPlans;
    },
    normalizeInvoice: ({ data: { data } }: AxiosResponse<{ data: UpgradeLicenseRespData }>): IInvoiceModel => {
      self.invoice = data.invoice;
      // self.lisenceInfo = data.license;
      //.step = 'upgrade-form';
      return self.invoice;
    },
    normalizeFeature: ({ data: { data } }: AxiosResponse<{ data: UpgradeLicenseRespData }>): IFeatureInfoModel => {
      self.featureData = data.featureData;
      return self.featureData;
    },
    normalizeLinceseInfo: ({ data }: AxiosResponse): ILicenseInfoModel | null => {
      self.lisenceInfo = data.subscription;
      return self.lisenceInfo;
    },
    normalizeAddonLinceseInfo: ({ data }: AxiosResponse): IAddonLicenseInfoModel | null => {
      self.addonInfo = data?.data;
      return self.addonInfo;
    },
    normalizeUpgradeLicenseInfo: ({ data }: AxiosResponse) => {
      self.upgradelicenseplan = data;
      return self.upgradelicenseplan;
    },
    normalizeUpgradeLicensePreviousInfo: ({ data }: AxiosResponse): IPreviousLicenseInfo | null => {
      self.previousLicense = data.data;
      return self.previousLicense;
    },
    setStep(step: 'upgrade-form' | 'payment-dialog' | 'payment-form'): void {
      self.step = step;
    },
    setPaymentMethod(method: 'card' | 'ach' | 'invoice'): void {
      self.paymentMethod = method;
    },
    setSelectedPlan(planId: number): number {
      self.selectedPlan = self.subscriptionPlans.find(({ id }) => id === planId);
      return self.selectedPlan?.id === self.currentPlan?.id
        ? self.currentPlan?.period.id || 0
        : self.selectedPlan?.periods[0].id || 0;
    },
  }))
  .actions((self) => ({
    getSubscriptionPlans: flow(function* () {
      const result = yield axios
        .get('admin/license-subscription/get-subscriptions')
        .then(self.normalizeSubscriptionPlans)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    upgradeLicense: flow(function* (upgradeLicenseData: UpgradeLicenseFormInput[]) {
      const result = yield axios
        .put('subscription', {
          upgradeLicenseData,
        })
        .then((response) => {
          self.normalizeInvoice(response);
          self.normalizeFeature(response);
        })
        .catch((error) => {
          throw new Error(error.response.data.message);
        });
      return result;
    }),
    postStripeACHToken: flow(function* (data: StripeACHResponse) {
      const result = yield axios
        .post('admin/license-subscription/ach', data)
        // .then(self.normalizeLinceseInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getLicenseInfo: flow(function* () {
      const result = yield axios
        .get<ILicenseInfoModel>('subscription')
        .then(self.normalizeLinceseInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getUpgradeLicenseInfo: flow(function* () {
      const result = yield axios
        .get(`subscription/pricing`)
        .then(self.normalizeUpgradeLicenseInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getCoreInfo: flow(function* () {
      const result = yield axios
        .get(`subscription/core-check`)
        .then(self.normalizeUpgradeLicensePreviousInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    billingDetail: flow(function* (billingDetailData: BillingInput) {
      const result = yield axios
        .post('admin/license-subscription/client-billing-details', {
          billingDetailData,
        })
        .catch((error) => {
          throw new Error(error.response.data.data);
        });
      return result;
    }),
    stripeResponce: flow(function* (stripeResponceData: any) {
      const result = yield axios
        .post('admin/license-subscription/update-payment-status', { ...stripeResponceData })
        .catch((error) => {
          throw new Error(error.response.data.data);
        });
      return result;
    }),
    getAddonDetail: flow(function* () {
      const result = yield axios
        .get(`subscription/addons`)
        .then(self.normalizeAddonLinceseInfo)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    submitUpgradeRequest: flow(function* (message: string) {
      const result = yield axios.post('subscription/upgrade-request', { message }).catch((error) => {
        throw new Error(error.response?.data?.message || 'Failed to submit upgrade request');
      });
      return result;
    }),
  }));

export type IUpgradeLicenseStore = Instance<typeof UpgradeLicenseStore>;
